import { createContext, ReactNode, useContext, useState } from 'react';

export interface HeaderConfig {
  allowClosePayment?: boolean;
  id: string;
  expiresAt?: number | null;
  merchantName?: string | null;
  billType?: string | null;
  setBillType?: (billType: string) => void;
  onMobileBackButtonClick?: () => void;
}

interface ContextProps extends HeaderConfig {
  configureHeader: (params: HeaderConfig) => void;
}

export const WidgetContext = createContext<ContextProps>({
  configureHeader: () => undefined,
  onMobileBackButtonClick: () => undefined,
  allowClosePayment: false,
  expiresAt: null,
  id: '',
  merchantName: '',
  billType: '',
});

export const WidgetProvider = ({ children }: { children: ReactNode }) => {
  const [expiresAt, setExpiresAt] = useState<HeaderConfig['expiresAt']>(null);
  const [id, setId] = useState<HeaderConfig['id']>('');
  const [allowClosePayment, setAllowClosePayment] = useState(false);
  const [merchantName, setMerchantName] =
    useState<HeaderConfig['merchantName']>('');
  const [billType, setBillType] = useState<HeaderConfig['billType']>('');
  const [onMobileBackButtonClick, setOnMobileBackButtonClick] = useState<
    HeaderConfig['onMobileBackButtonClick']
  >(() => undefined);

  const configureHeader = ({
    allowClosePayment,
    expiresAt,
    id,
    merchantName,
    billType,
    onMobileBackButtonClick,
  }: HeaderConfig) => {
    setAllowClosePayment(allowClosePayment || false);
    setExpiresAt(expiresAt);
    setId(id);
    setMerchantName(merchantName);
    setBillType(billType);
    setOnMobileBackButtonClick(onMobileBackButtonClick);
  };

  const value: ContextProps = {
    allowClosePayment,
    expiresAt,
    id,
    merchantName,
    billType,
    configureHeader,
    onMobileBackButtonClick,
  };

  return (
    <WidgetContext.Provider value={value}>{children}</WidgetContext.Provider>
  );
};

export const useWidgetContext = () => {
  const context = useContext(WidgetContext);

  return context;
};
